import React, { useEffect, useMemo, useState } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowUpDown, UserPlus, Edit2, Trash2, List, Network } from 'lucide-react';
import Shell from '../components/TopBarShell';
import EmployeeAvatar from '../components/EmployeeAvatar';
import EmployeeComposer from '../components/EmployeeComposer';
import OrgChart from '../components/OrgChart';
import SlackLink from '../components/SlackLink';
import CopyButton from '../components/CopyButton';
import { Button } from '../components/ui/button';
import { useReviewQueue } from '../contexts/ReviewQueueContext';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { deptLabel } from '../lib/departments';
import api from '../utils/api';

const COL_DEFS = [
  { key: 'full_name',    labelKey: 'directory.col.collaborator', width: 'w-[20%]' },
  { key: 'poste',        labelKey: 'directory.col.role',         width: 'w-[13%]' },
  { key: 'departement',  labelKey: 'directory.col.department',   width: 'w-[12%]' },
  { key: 'localisation', labelKey: 'directory.col.office',       width: 'w-[8%]'  },
  { key: 'email',        labelKey: 'directory.col.email',        width: 'w-[19%]' },
  { key: 'telephone',    labelKey: 'directory.col.phone',        width: 'w-[15%]', sortable: false },
  { key: 'manager',      labelKey: 'directory.col.manager',      width: 'w-[9%]',  sortable: false },
];

const Directory = () => {
  const { count: reviewCount } = useReviewQueue();
  const t = useT();
  const { locale } = useLocale();
  const ALL = t('common.all');
  const [employees, setEmployees] = useState(null);
  const [isHR, setIsHR] = useState(false);
  const [q, setQ] = useState('');
  const [activeDept, setActiveDept] = useState(ALL);
  const [sortKey, setSortKey] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState('list');  // 'list' | 'org'

  useEffect(() => {
    api.employees().then(setEmployees).catch(() => setEmployees([]));
    api.isHR().then(setIsHR).catch(() => setIsHR(false));
  }, []);

  const { depts, rows } = useMemo(() => {
    if (!employees) return { depts: [], rows: null };

    const counts = new Map();
    counts.set(ALL, employees.length);
    for (const e of employees) {
      const k = e.departement || 'Sans département';
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    const depts = [...counts.entries()].sort((a, b) =>
      a[0] === ALL ? -1 : b[0] === ALL ? 1 : a[0].localeCompare(b[0]));

    const ql = q.trim().toLowerCase();
    let list = employees
      .filter(e => activeDept === ALL || (e.departement || 'Sans département') === activeDept)
      .filter(e => !ql || [e.full_name, e.email, e.poste, e.localisation, e.telephone]
        .some(v => (v || '').toLowerCase().includes(ql)));

    list = list.slice().sort((a, b) => {
      const av = (a[sortKey] || '').toLowerCase();
      const bv = (b[sortKey] || '').toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

    return { depts, rows: list };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, q, activeDept, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const create = async (payload) => {
    const created = await api.createEmployee(payload);
    setEmployees(arr => [...(arr || []), created]);
    setComposing(false);
  };
  const update = async (payload) => {
    const updated = await api.updateEmployee(editing.id, payload);
    setEmployees(arr => arr.map(x => x.id === updated.id ? updated : x));
    setEditing(null);
  };
  const remove = async (emp) => {
    if (!window.confirm(t('emp.confirmDelete', { name: emp.full_name }))) return;
    await api.deleteEmployee(emp.id);
    setEmployees(arr => arr.filter(x => x.id !== emp.id));
  };

  return (
    <Shell reviewCount={reviewCount} wide>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-[1.75rem]">{t('directory.title')}</h1>
          <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
            {t('directory.intro')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
            <button type="button" onClick={() => setView('list')}
              className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition ${
                view === 'list'
                  ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                  : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))]'
              }`}
              aria-pressed={view === 'list'}>
              <List className="h-3.5 w-3.5" /> {t('directory.view.list')}
            </button>
            <button type="button" onClick={() => setView('org')}
              className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition border-l border-border ${
                view === 'org'
                  ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                  : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))]'
              }`}
              aria-pressed={view === 'org'}>
              <Network className="h-3.5 w-3.5" /> {t('directory.view.org')}
            </button>
          </div>
          {view === 'list' && (
            <label className="relative w-72">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('directory.searchPh')}
                className="input !pl-9"
              />
            </label>
          )}
          {isHR && !composing && !editing && (
            <Button onClick={() => setComposing(true)} className="gap-2">
              <UserPlus className="h-4 w-4" /> {t('common.add')}
            </Button>
          )}
        </div>
      </header>

      {composing && (
        <EmployeeComposer existing={employees || []} onSave={create} onCancel={() => setComposing(false)} />
      )}
      {editing && (
        <EmployeeComposer
          existing={(employees || []).filter(e => e.id !== editing.id)}
          initial={editing}
          mode="edit"
          onSave={update}
          onCancel={() => setEditing(null)}
        />
      )}

      {view === 'org' && (
        <OrgChart employees={employees || []} onEdit={isHR ? setEditing : undefined} />
      )}

      {view === 'list' && (<>
      {/* Department pills */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        {depts.map(([name, n]) => (
          <button
            key={name}
            onClick={() => setActiveDept(name)}
            className={[
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] transition',
              activeDept === name
                ? 'border-[hsl(var(--text))] bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                : 'border-border bg-[hsl(var(--surface))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] hover:border-[hsl(var(--rule-strong))]',
            ].join(' ')}
          >
            <span>{name === ALL ? name : deptLabel(name, locale)}</span>
            <span className={`text-[11px] ${activeDept === name ? 'opacity-70' : 'text-[hsl(var(--text-muted))]'}`}>{n}</span>
          </button>
        ))}
      </div>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1080px] text-[14px] table-fixed">
          <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))] border-b border-border">
            <tr>
              {COL_DEFS.map(c => (
                <th key={c.key} className={`text-left font-medium px-4 py-3 ${c.width}`}>
                  {c.sortable === false ? (
                    <span>{t(c.labelKey)}</span>
                  ) : (
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-[hsl(var(--text))]">
                      <span>{t(c.labelKey)}</span>
                      <SortIcon active={sortKey === c.key} dir={sortDir} />
                    </button>
                  )}
                </th>
              ))}
              {isHR && <th className="w-[6%]" />}
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={COL_DEFS.length + (isHR ? 1 : 0)} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</td></tr>
            )}
            {rows !== null && rows.length === 0 && (
              <tr><td colSpan={COL_DEFS.length + (isHR ? 1 : 0)} className="px-4 py-10 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.empty')}</td></tr>
            )}
            {rows && rows.map(m => (
              <tr key={m.id} className="group border-t border-[hsl(var(--rule))] hover:bg-[hsl(var(--surface-2))]">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <EmployeeAvatar fullName={m.full_name} email={m.email} photo={m.photo} size={32} />
                    <span className="font-medium truncate">{m.full_name}</span>
                    <SlackLink slackUserId={m.slack_user_id} size={13} className="opacity-60 hover:opacity-100" />
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[hsl(var(--text-soft))] truncate">{m.poste || '·'}</td>
                <td className="px-4 py-2.5 text-[hsl(var(--text-soft))] truncate">{m.departement ? deptLabel(m.departement, locale) : '·'}</td>
                <td className="px-4 py-2.5 text-[hsl(var(--text-soft))] truncate">{m.localisation || '·'}</td>
                <td className="px-4 py-2.5 min-w-0">
                  {m.email ? (
                    <div className="flex items-center gap-1 min-w-0">
                      <a href={`mailto:${m.email}`} title={m.email} className="font-num text-[13px] truncate hover:text-[hsl(var(--accent))]">{m.email}</a>
                      <CopyButton value={m.email} label={t('common.copyEmail')} />
                    </div>
                  ) : <span className="text-[hsl(var(--text-muted))]">·</span>}
                </td>
                <td className="px-4 py-2.5">
                  {m.telephone ? (
                    <div className="flex items-center gap-1">
                      <a href={`tel:${m.telephone.replace(/[^+0-9]/g, '')}`} className="font-num text-[13px] text-[hsl(var(--text-soft))] whitespace-nowrap hover:text-[hsl(var(--accent))]">{m.telephone}</a>
                      <CopyButton value={m.telephone} label={t('common.copyPhone')} />
                    </div>
                  ) : <span className="text-[hsl(var(--text-muted))]">·</span>}
                </td>
                <td className="px-4 py-2.5 min-w-0">
                  {(m.manager?.full_name || m.manager_name) ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      {m.manager && <EmployeeAvatar fullName={m.manager.full_name} email={m.manager.email} photo={m.manager.photo} size={20} />}
                      <span className="truncate text-[13px]">{m.manager?.full_name || m.manager_name}</span>
                    </div>
                  ) : (
                    <span className="text-[hsl(var(--text-muted))]">·</span>
                  )}
                </td>
                {isHR && (
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setEditing(m)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label="Modifier">
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(m)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label="Supprimer">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows && rows.length > 0 && (
        <div className="text-[13px] text-[hsl(var(--text-muted))] mt-3">
          {t(rows.length > 1 ? 'directory.count.many' : 'directory.count', { n: rows.length })}{activeDept !== ALL ? ` · ${deptLabel(activeDept, locale)}` : ''}
        </div>
      )}
      </>)}
    </Shell>
  );
};

const SortIcon = ({ active, dir }) => {
  if (!active) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
  return dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
};

export default Directory;
